import type {
  AgentProgressEvent,
  AgentRunActivity,
  TicketWorkflow,
  TicketWorkflowActivity,
} from "./automation";
import type { BoardEvent } from "./sse";

export interface ActivityTick {
  workflowId: string;
  runId: string;
  phase: string;
  headline: string;
  kind: string;
  level: string;
  eventSequence: number;
  occurredAt: string;
}

export interface NowTileModel {
  workflowId: string;
  runId: string;
  phase: string;
  role: string;
  monogram: string;
  headline: string;
  level: string;
  startedAt: string;
  state: string;
  events: AgentProgressEvent[];
}

const PHASES: Record<string, { role: string; monogram: string }> = {
  product_manager: { role: "Product Manager", monogram: "PM" },
  approver: { role: "Approver", monogram: "AP" },
  engineer: { role: "Engineer", monogram: "EN" },
  engineer_revision: { role: "Engineer", monogram: "EN" },
  qa: { role: "QA", monogram: "QA" },
};

function text(value: unknown, limit = 2_000): string {
  return typeof value === "string" ? value.trim().slice(0, limit) : "";
}

function integer(value: unknown): number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0 ? value : 0;
}

export function parseActivityTick(event: BoardEvent): ActivityTick | null {
  if (event.event_type !== "agent_run.activity") return null;
  const metadata = event.metadata || {};
  const workflowId = text(metadata.workflow_id, 80);
  const runId = text(metadata.run_id, 80);
  const phase = text(metadata.phase, 40);
  const headline = text(metadata.headline);
  const occurredAt = text(metadata.occurred_at, 80);
  if (!workflowId || !runId || !phase || !headline || !occurredAt) return null;
  return {
    workflowId,
    runId,
    phase,
    headline,
    kind: text(metadata.kind, 40) || "progress",
    level: text(metadata.level, 20) || "info",
    eventSequence: integer(metadata.event_sequence),
    occurredAt,
  };
}

export function isTicketConversationEvent(event: BoardEvent): boolean {
  return (
    event.event_type === "agent_run.activity" ||
    event.event_type.startsWith("agent_run.") ||
    event.event_type.startsWith("comment.") ||
    event.event_type.startsWith("ticket_workflow.")
  );
}

function phaseFromStatus(status: string): string {
  if (status.startsWith("product_manager")) return "product_manager";
  if (status.startsWith("approver")) return "approver";
  if (status.startsWith("engineer_revision")) return "engineer_revision";
  if (status.startsWith("engineer")) return "engineer";
  if (status.startsWith("qa")) return "qa";
  return "agent";
}

function presentation(phase: string): { role: string; monogram: string } {
  return PHASES[phase] || {
    role: phase
      .split("_")
      .filter(Boolean)
      .map((part) => part[0]?.toUpperCase() + part.slice(1))
      .join(" "),
    monogram: phase.slice(0, 2).toUpperCase() || "AI",
  };
}

function defaultHeadline(phase: string, state: string): string {
  const role = presentation(phase).role;
  return state === "queued" || state.endsWith("_queued")
    ? `Waiting to start ${role}`
    : `${role} is working through the ticket`;
}

function headline(value: string): string {
  const clean = value.trim();
  return clean.startsWith("Focus: ") ? clean.slice(7) : clean;
}

function eventIdentity(event: AgentProgressEvent): string {
  return event.id || `${event.run_id}:${event.sequence}`;
}

export function mergeWorkflowActivity(
  current: TicketWorkflowActivity | null,
  incoming: TicketWorkflowActivity,
): TicketWorkflowActivity {
  if (!current || current.workflow_id !== incoming.workflow_id) return incoming;
  const runs = new Map<string, AgentRunActivity>();
  for (const run of [...current.runs, ...incoming.runs]) runs.set(run.id, run);
  const events = new Map<string, AgentProgressEvent>();
  for (const event of [...current.events, ...incoming.events]) {
    events.set(eventIdentity(event), event);
  }
  return {
    ...current,
    ...incoming,
    runs: [...runs.values()].sort(
      (left, right) =>
        new Date(left.requested_at).getTime() - new Date(right.requested_at).getTime(),
    ),
    events: [...events.values()].sort((left, right) => {
      const time = new Date(left.occurred_at).getTime() - new Date(right.occurred_at).getTime();
      return time || left.run_id.localeCompare(right.run_id) || left.sequence - right.sequence;
    }),
    total_events: Math.max(current.total_events, incoming.total_events),
    events_truncated: current.events_truncated || incoming.events_truncated,
  };
}

export function nowTileModel(
  workflow: TicketWorkflow | null,
  activity: TicketWorkflowActivity | null,
  tick: ActivityTick | null,
): NowTileModel | null {
  if (!workflow?.current_run_id) return null;
  const runId = workflow.current_run_id;
  const activeTick =
    tick && tick.workflowId === workflow.id && tick.runId === runId ? tick : null;
  const run = activity?.runs.find((item) => item.id === runId);
  const events = (activity?.events || []).filter((event) => event.run_id === runId);
  const latest = events[events.length - 1];
  const phase = activeTick?.phase || run?.phase || latest?.phase || phaseFromStatus(workflow.status);
  const role = presentation(phase);
  const state = run?.status || workflow.status;
  return {
    workflowId: workflow.id,
    runId,
    phase,
    role: role.role,
    monogram: role.monogram,
    headline: headline(
      activeTick?.headline || latest?.message || defaultHeadline(phase, state),
    ),
    level: activeTick?.level || latest?.level || "info",
    startedAt: run?.started_at || run?.requested_at || workflow.updated_at,
    state,
    events: events.slice(-12),
  };
}

export function latestSequence(
  activity: TicketWorkflowActivity | null,
  runId: string,
): number {
  return Math.max(
    0,
    ...(activity?.events || [])
      .filter((event) => event.run_id === runId)
      .map((event) => event.sequence),
  );
}

export function formatElapsed(startedAt: string, now = Date.now()): string {
  const started = new Date(startedAt).getTime();
  if (!Number.isFinite(started)) return "just now";
  const seconds = Math.max(0, Math.floor((now - started) / 1_000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${String(seconds % 60).padStart(2, "0")}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${String(minutes % 60).padStart(2, "0")}m`;
}

export class HeadlineCoalescer<T> {
  private readonly intervalMs: number;
  private lastAppliedAt = 0;
  private pending: T | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(intervalMs = 2_000) {
    this.intervalMs = intervalMs;
  }

  push(value: T, apply: (next: T) => void): void {
    const now = Date.now();
    const remaining = this.intervalMs - (now - this.lastAppliedAt);
    if (!this.lastAppliedAt || remaining <= 0) {
      this.cancelTimer();
      this.lastAppliedAt = now;
      apply(value);
      return;
    }
    this.pending = value;
    if (this.timer) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      const next = this.pending;
      this.pending = null;
      if (next === null) return;
      this.lastAppliedAt = Date.now();
      apply(next);
    }, remaining);
  }

  cancel(): void {
    this.pending = null;
    this.cancelTimer();
    this.lastAppliedAt = 0;
  }

  private cancelTimer(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }
}
